/**
 * Returns the pluralized form of the word, based on the provided count.
 * This is essentially just a slightly nicer wrapper around a ternary expression,
 * with some minor convenience upgrades. Does not handle complex cases,
 * such as: "daisy" => "daisies". For these cases, provide the 3rd "pluralized" param.
 *
 *
 * @param {number} count The count of things to which the given word refers
 * @param {string} singular The singular form of the word
 * @param {string} pluralized The plural form of the word. If omitted, will be `singular` with an `s` appended.
 *
 * @example
 * plural(1, 'test'); // => 'test'
 * plural(2, 'test'); // => 'tests'
 * plural(1, 'boss', 'bosses'); // => 'boss'
 * plural(2, 'boss', 'bosses'); // => 'bosses'
 *
 * @see https://www.npmjs.com/package/pluralize - The package this is based on.
 */
export function plural(count: number, singular: string, pluralized?: string): string {
  //  This handles cases where the consumer may have accidentally passed in a falsy value.
  //  We do this to prevent a single `s' string from being returned.
  if (!singular) {
    return '';
  }
  if (count === 1) {
    return singular;
  }
  return pluralized || `${singular}s`;
}

// Helpers for a few common pluralizations.
export const pluralizeIssues = (count: number) => plural(count, 'issue');
export const pluralizeDependencies = (count: number) => plural(count, 'dependency', 'dependencies');
export const pluralizeIsAre = (count: number) => plural(count, 'is', 'are');
